import Meta from '../../models/Meta';
import CountData from '../../models/CountData';
import PrefixData, { formatPrefixData } from '../../models/PrefixData';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import utc from 'dayjs/plugin/utc';

import router from '../../router';
import welcomeTourData from '../../constants/welcomeTourData/index';
import { formatHitsRes, formatSummaryRes } from '../../utils/responseFormatter';
import {
  PATH,
  MAX_QUERY_HISTORY_ENTRIES_APNIC_STAFF,
  LOCAL_STORAGE_ITEM_LIST
} from '../../constants/common';
import requestInstance from '../../utils/requestInstance';
import { getPageIndex } from '../../utils/common';
import themeDataListRequestInstance from '../../utils/themeDataListRequestInstance';
import { isNonProdEnv, profileSectionURLs } from '../../config/oidc';

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(utc);

const format = 'YYYY-MM-DD';
const NUMBER_OF_DAYS = 120;
let updateAlertsDataPreviousExecutionTimestamp = new Date().getTime();
let updateAlertsDataPreviousExecutionASN = '';
const UPDATE_ALERTS_DATA_MIN_EXECUTION_INTERVAL = 1000;

// DECLARE!!! ::::
// since this is a read only app (at the moment), none object is deep copied
const state = {
  month: 'this', // this last interval
  startAndEndDate: getStartAndEndDate('this'),
  accountList: [],
  asnList: [],
  selectedAccount: null,
  selectedASN: null,
  displayType: 'economy', // economy or region
  members: null,
  regionData: [], // history record, prevent extra data fetch
  economyData: [], // history record
  loadingData: false,
  noData: false,
  welcomeTourMode: false,
  metaDataFetched: false, // differentiate user has account or no associated account,
  recurringExpression: '',
  recurringPeriod: 30,
  recurringLastDate: null,
  prefixDataList: [], // history record to avoid retrieve data by api repeatedly
  memberAccountQueryOfAPNICStaff: '',
  themeDataList: []
};

const getters = {
  welcomeTourMode: state => {
    return state.welcomeTourMode;
  },
  noData: state => {
    if (state.welcomeTourMode) {
      return false;
    }
    return state.noData;
  },
  displayType: state => {
    return state.displayType;
  },
  month: state => {
    return state.month;
  },
  accountList: state => {
    if (state.welcomeTourMode) {
      return welcomeTourData.accountList;
    }
    return state.accountList;
  },
  asnList: state => {
    if (state.welcomeTourMode) {
      return welcomeTourData.asnList;
    }
    return state.asnList;
  },
  selectedASN: state => {
    if (state.welcomeTourMode) {
      return welcomeTourData.selectedASN;
    }
    return state.selectedASN;
  },
  selectedAccount: state => {
    if (state.welcomeTourMode) {
      return welcomeTourData.selectedAccount;
    }
    return state.selectedAccount;
  },
  asnData: (state, getters) => {
    if (
      getters.selectedASN &&
      getters.selectedASN.data &&
      getters.selectedASN.data.data
    ) {
      return filterAndParse(getters.selectedASN.data.data, state.month);
    }
  },
  economyData: (state, getters) => {
    if (
      getters.selectedAccount &&
      getters.selectedAccount.economyData &&
      getters.selectedAccount.economyData.data
    ) {
      return filterAndParse(
        getters.selectedAccount.economyData.data,
        state.month
      );
    }
  },
  regionData: (state, getters) => {
    if (
      getters.selectedAccount &&
      getters.selectedAccount.regionData &&
      getters.selectedAccount.regionData.data
    ) {
      return filterAndParse(
        getters.selectedAccount.regionData.data,
        state.month
      );
    }
  },
  getNameByType: (state, getters) => type => {
    if (getters.selectedASN && getters.selectedAccount) {
      switch (type) {
        case 'asn':
          return getters.selectedASN.asn;
        case 'economy':
          return getters.selectedAccount.economyName;
        case 'region':
          return getters.selectedAccount.region;
        default:
          return 'N/A';
      }
    } else {
      switch (type) {
        case 'asn':
          return 'origin AS';
        case 'economy':
          return 'your economy';
        case 'region':
          return 'your region';
        default:
          return 'N/A';
      }
    }
  },
  accumulativeStatsData: (state, getters) => {
    // Only update data when users are on "Suspicious traffic" page, i.e. selectedASN.data exists

    if (
      getters.selectedASN &&
      getters.selectedAccount &&
      getters.selectedASN.data
    ) {
      // Only update data when selectedASN exists in selectedAccount
      if (
        isSelectedASNExistsInSelectedAccount(
          getters.selectedASN,
          getters.selectedAccount
        )
      ) {
        const response = calculateAccumulativeStats(state.month, getters);
        return response;
      }
    }
  },
  getPrefixDataOfDashServer: (state, getters) => {
    return getters.selectedASN.prefix;
  },
  getPrefixData: (state, getters) => type => {
    // ipv4, ipv6
    let data = [];
    let sum = 0;
    let seen = 0;
    if (getters.selectedASN) {
      const filteredData = filterData(
        getters.selectedASN.prefix.data,
        state.month
      );

      filteredData.forEach(i => {
        if (i[type + 'Routed']) {
          sum += i[type + 'Routed'].bgp;
          seen += i[type + 'Routed'].seen;
        }
        i[type].forEach(j => {
          const existPrefix = data.find(
            k => k.prefix === j.prefix && k.protocol === j.protocol
          );
          if (existPrefix) {
            existPrefix.count += j.count;
            // merge source ip
            // add up counts for same ip and port
            j.sourceIP.forEach(x => {
              const existIP = existPrefix.sourceIP.find(
                y => y.ip === x.ip && y.port === x.port
              );
              if (existIP) {
                existIP.count += x.count;
              } else {
                existPrefix.sourceIP.push(x);
              }
            });
          } else {
            // deep clone the prefix data
            data.push(JSON.parse(JSON.stringify(j)));
          }
        });
      });
      data.sort((a, b) => (a.count > b.count ? -1 : 1));
      data.forEach(i =>
        i.sourceIP.sort((a, b) => (a.count > b.count ? -1 : 1))
      );
    }
    return { data, sum, seen };
  },
  noASN: (state, getters) => getters.asnList.length === 0,
  noAccount: (state, getters) => getters.accountList.length === 0,
  dataReady: (state, getters) =>
    state.metaDataFetched && state.members !== null,
  loadingData: state => state.loadingData,
  metaDataFetched: state => state.metaDataFetched,
  recurringExpression: state => state.recurringExpression,
  recurringPeriod: state => state.recurringPeriod,
  recurringLastDate: state => state.recurringLastDate,
  numberOfDaysShowingData: state => {
    return state.month === 'this' ? 30 : 60;
  },
  prefixDataArray: (state, getters) =>
    getters.selectedASN
      ? getters.selectedASN.prefix
        ? getters.selectedASN.prefix.data
        : []
      : []
};

const mutations = {
  setWelcomeTourMode: (state, bool) => {
    state.welcomeTourMode = bool;
  },
  setDisplayType: (state, displayType) => {
    state.displayType = displayType;
  },
  setMonth: (state, month) => {
    state.month = month;
    state.startAndEndDate = getStartAndEndDate(month);
  },
  // init all meta data after login
  setInitState: (state, members) => {
    const accountList = [];
    members.forEach(i => {
      if (!accountList.some(j => j.id === i.id)) {
        accountList.push(i);
      }
      return {
        account: i.account,
        id: i.id
      };
    });

    state.accountList = accountList;
    state.members = members;
  },
  setSelectedAccount: (state, member) => {
    state.selectedAccount = member;
  },
  setSelectedASN: (state, ASN) => {
    state.noData = false;
    state.selectedASN = ASN;
    // handle member account has no linked asn
    if (!ASN || !ASN.data) {
      return;
    }

    if (ASN.data.totalCount === 0) {
      state.noData = true;
    }

    triggerGTM('LoggedInUserType', {
      hasAccount: true,
      hasData: !state.noData
    });
  },
  setASNList: (state, list) => {
    state.asnList = list.sort((a, b) => (+a.id > +b.id ? 1 : -1));
  },
  addRegionData: (state, region) => {
    state.regionData.push(region);
  },
  addEconomyData: (state, economy) => {
    state.economyData.push(economy);
  },
  setLoadingData: (state, boolean) => {
    state.loadingData = boolean;
  },
  setMetaDataFetched: (state, boolean) => {
    state.metaDataFetched = boolean;
  },
  setRecurringExpression: (state, recurringExpression) => {
    state.recurringExpression = recurringExpression;
  },
  setRecurringPeriod: (state, recurringPeriod) => {
    state.recurringPeriod = recurringPeriod;
  },
  setRecurringLastDate: (state, recurringLastDate) => {
    state.recurringLastDate = recurringLastDate;
  },
  addPrefixData: (state, prefix) => {
    state.prefixDataList.push(prefix);
  },
  setMemberAccountQueryOfAPNICStaff: (state, query) => {
    state.memberAccountQueryOfAPNICStaff = query;
  },
  setThemeDataList: (state, data) => {
    state.themeDataList = data;
  }
};

const actions = {
  changeDisplayType: ({ commit }, type) => {
    commit('setDisplayType', type);
  },
  changeMonth: (context, month) => {
    context.commit('setMonth', month);
    context.dispatch('selectASN', context.state.selectedASN.id);
  },
  fetchMetaData: ({ commit, dispatch }) => {
    commit('setLoadingData', true);
    commit('setMetaDataFetched', false);
    commit('setIsShowErrorInfoBanner', false, { root: true });
    commit('setIsSuccessfulSummaryRequest', false, { root: true });

    requestInstance
      .get('/summary/')
      .then(res => {
        commit('setLoadingData', false);
        commit('setIsSuccessfulSummaryRequest', true, { root: true });

        const formattedResponse = formatSummaryRes(res.data);
        const data = new Meta(formattedResponse);

        commit('setInitState', data.members);

        if (data.members.length > 0 && !state.selectedAccount) {
          dispatch('selectAccount', data.members[0].id);
        }
        // trigger GTM here
        if (data.members.length === 0) {
          triggerGTM('LoggedInUserType', { hasAccount: false });
        }
        commit('setMetaDataFetched', true);

        const width = window.innerWidth;
        if (width > 768) {
          commit('setShowSideBar', true, { root: true });
        }

        if (
          process.env.VUE_APP_REPORT_MODE &&
          router.currentRoute.name === 'authCallback'
        ) {
          router.push('/suspicious-traffic');
        }
      })
      .catch(err => {
        console.error(err);
        commit('setLoadingData', false);
        commit('setMetaDataFetched', true);
        dispatch('setErrorInfoBanner', '', { root: true });

        if (process.env.VUE_APP_REPORT_MODE) {
          router.push('/suspicious-traffic');
          throw `Error from summary request: ${err.toString()}`;
        } else {
          router.push('/routing-status');
        }
      });
  },
  selectAccount: (context, memId) => {
    // account should always exist
    const account = context.state.members.find(i => i.id === memId);
    triggerGTM('MemberAccountSelected', {
      memberID: account.id,
      memberName: account.account
    });
    context.commit('setASNList', account.ASNs);

    context.commit('setSelectedAccount', account);

    const currentPath = getPath();

    let pageIndex = getPageIndex(currentPath);

    if (process.env.VUE_APP_REPORT_MODE) {
      if (account.ASNs.length > 0) {
        // select the 1st element in ASNs after changing account
        context.dispatch('selectASN', account.ASNs[0].id);
      } else {
        // if no asn linked, clear data
        context.commit('setSelectedASN', null);
      }
    } else if (
      // Check isNavigatingToRSPageAfterLogin along with pageIndex to solve the possible issue that routing status page showing an ASN rather than "your prefix", which is caused by page being navigated from callback to routing-status later than getting summary response.
      pageIndex === PATH.ROUTING_STATUS ||
      context.rootState.isNavigatingToRSPageAfterLogin
    ) {
      // default context on Routing status page is "your prefixes"
      context.commit('setSelectedASN', null);
      context.dispatch('updateAlertsData', null);

      context.dispatch(
        'routingStatusStore/fetchRoutingStatusDataOfPrefixes',
        null,
        { root: true }
      );
    } else if (
      pageIndex === PATH.SUSPICIOUS_TRAFFIC ||
      pageIndex === PATH.ALERTS_SUSPICIOUS_TRAFFIC ||
      pageIndex === PATH.OTHERS
    ) {
      if (account.ASNs.length > 0) {
        // select the 1st element in ASNs after changing account
        context.dispatch('selectASN', account.ASNs[0].id);
      } else {
        // if no asn linked, clear data
        context.commit('setSelectedASN', null);
        context.dispatch('updateAlertsData', null);
      }
    } else if (pageIndex === PATH.ALERTS_ROUTING_STATUS) {
      context.commit('setSelectedASN', null);
      context.dispatch('updateAlertsData', null);

      context.dispatch(
        'alertsRoutingStatusStore/fetchMemberContactsByMemberAccountName',
        { accountName: account.account, memberId: account.id },
        { root: true }
      );
      context.dispatch(
        'alertsRoutingStatusStore/fetchAlertsRoutingStatusSummary',
        null,
        { root: true }
      );
      context.dispatch(
        'alertsRoutingStatusStore/fetchDelegatedPrefixesByMemberId',
        account.id,
        { root: true }
      );
    }
  },
  selectASN: (context, asnId) => {
    const memberId = context.state.selectedAccount.id;
    const account = context.state.members.find(i => i.id === memberId);
    const asn = context.state.asnList.find(i => i.id === asnId);

    context.commit('setSelectedASN', asn);
    context.dispatch('updateAlertsData', asn);

    const currentPath = getPath();
    let pageIndex = getPageIndex(currentPath);

    // To fetch suspicious traffic data when it is on page "suspicious-traffic" or it is in DASH-Report mode
    if (
      pageIndex === PATH.SUSPICIOUS_TRAFFIC ||
      process.env.VUE_APP_REPORT_MODE
    ) {
      context.dispatch('fetchSuspiciousTrafficData', {
        memberId,
        account,
        asn
      });

      if (!process.env.VUE_APP_REPORT_MODE) {
        context.dispatch('fetchRecurringReport');
      }
    } else if (pageIndex === PATH.ROUTING_STATUS) {
      context.dispatch(
        'routingStatusStore/fetchRoutingStatusDataOfSelectedASN',
        { asn, memberId },
        { root: true }
      );
    }
  },
  fetchSuspiciousTrafficData: (context, { memberId, account, asn }) => {
    if (!asn.data) {
      // when asn.data does not exist, retrieve asn hits, economy hits and region hits by api /hits, and retrieve prefix hits by api /prefix
      const { economy, economyName, id: asnId, region } = asn;
      const numberOfDays = NUMBER_OF_DAYS;
      const numberOfDaysShowingPrefixData =
        context.getters.numberOfDaysShowingData;

      const hitsRequest = buildHitsRequest({
        memberId,
        asn: asnId,
        economy,
        region,
        numberOfDays
      });

      const prefixRequest = buildPrefixRequest({
        memberId,
        asn: asnId,
        numberOfDays: numberOfDaysShowingPrefixData
      });

      context.commit('setLoadingData', true);
      context.commit('setIsShowErrorInfoBanner', false, { root: true });

      Promise.all([hitsRequest, prefixRequest])
        .then(res => {
          context.commit('setLoadingData', false);

          const defaultStartDate = dayjs()
            .subtract(numberOfDays, 'day')
            .format(format);
          const {
            economyHits = [],
            originAsHits = [],
            regionHits = [],
            start = defaultStartDate
          } = res[0].data;
          const economyHitsFormatted = formatHitsRes(economyHits, start);
          const regionHitsFormatted = formatHitsRes(regionHits, start);
          const originAsHitsFormatted = formatHitsRes(originAsHits, start);

          // Update account.economy, account.region, account.economyData and account.regionData
          const economyData = new CountData(economyHitsFormatted, economy);
          const regionData = new CountData(regionHitsFormatted, region);

          account.economy = economy;
          account.economyName = economyName;
          account.region = region;
          account.economyData = economyData;
          account.regionData = regionData;

          // Add economyData and regionData into state.economyData<Array> and state.regionData<Array>, if they haven't been stored before
          if (!context.state.economyData.some(item => item.id === economy))
            context.commit('addEconomyData', economyData);
          if (!context.state.regionData.some(item => item.id === economy))
            context.commit('addRegionData', regionData);

          // Add asn hits data into asn
          const asnData = new CountData(originAsHitsFormatted, asnId);
          asn.data = asnData;

          // Add prefix hits data into asn
          const prefixData = new PrefixData(
            res[1].data,
            numberOfDaysShowingPrefixData
          );

          const formattedPrefixData = formatPrefixData(prefixData.data);
          asn.prefix = {
            data: formattedPrefixData,
            numberOfDaysShowingPrefixData
          };

          context.commit('addPrefixData', {
            data: formattedPrefixData,
            numberOfDaysShowingPrefixData,
            asn: asnId
          });

          context.commit('setSelectedASN', asn);

          context.commit('setSelectedAccount', account);
        })
        .catch(err => {
          context.commit('setLoadingData', false);
          context.dispatch('setErrorInfoBanner', '', { root: true });
          console.error(err);

          if (process.env.VUE_APP_REPORT_MODE) {
            throw `Error from hits request or prefix request: ${err.toString()}`;
          }
        });
    } else {
      // when asn.data exists, check asn.prefix and asn.prefix.numberOfDaysShowingPrefixData
      // if asn.prefix not exist or asn.prefix.numberOfDaysShowingPrefixData is not equal to current selected period (30 days or 60 days), retrieve asn.prefix data

      if (
        !asn.prefix ||
        asn.prefix.numberOfDaysShowingPrefixData !==
          context.getters.numberOfDaysShowingData
      ) {
        const { id: asnId } = asn;

        // check if prefixDataList contains elements with the specific asnId and numberOfDaysShowingPrefixData
        // if yes, retrieve asn.prefix from prefixDataList directly; otherwise, retrieve data by api /prefix
        const prefixDataInList = context.state.prefixDataList.find(
          i =>
            i.asn === asnId &&
            i.numberOfDaysShowingPrefixData ===
              context.getters.numberOfDaysShowingData
        );

        if (prefixDataInList) {
          asn.prefix = {
            data: prefixDataInList.data,
            numberOfDaysShowingPrefixData:
              prefixDataInList.numberOfDaysShowingPrefixData
          };

          context.commit('setSelectedASN', asn);

          context.commit('setSelectedAccount', account);
        } else {
          // retrieve prefix hits by api /prefix
          const numberOfDaysShowingPrefixData =
            context.getters.numberOfDaysShowingData;

          const prefixRequest = buildPrefixRequest({
            memberId,
            asn: asnId,
            numberOfDays: numberOfDaysShowingPrefixData
          });

          context.commit('setLoadingData', true);

          prefixRequest
            .then(res => {
              context.commit('setLoadingData', false);

              const prefixData = new PrefixData(
                res.data,
                numberOfDaysShowingPrefixData
              );
              const formattedPrefixData = formatPrefixData(prefixData.data);
              asn.prefix = {
                data: formattedPrefixData,
                numberOfDaysShowingPrefixData
              };

              context.commit('addPrefixData', {
                data: formattedPrefixData,
                numberOfDaysShowingPrefixData,
                asn: asnId
              });

              context.commit('setSelectedASN', asn);

              context.commit('setSelectedAccount', account);
            })
            .catch(err => {
              context.commit('setLoadingData', false);
              console.error(err);
            });
        }
      } else {
        // hits data has been retrieved before, so update account economyData and regionData from state.economyData<Array> and state.regionData<Array>
        const { economy, economyName, region } = asn;
        const economyData = context.state.economyData.find(
          item => item.id === economy
        );
        const regionData = context.state.regionData.find(
          item => item.id === region
        );

        account.economy = economy;
        account.economyName = economyName;
        account.economyData = economyData;
        account.region = region;
        account.regionData = regionData;

        context.commit('setSelectedASN', asn);

        context.commit('setSelectedAccount', account);
      }
    }
  },
  startWelcomeTour: ({ commit }) => {
    commit('setWelcomeTourMode', true);
  },
  finishWelcomeTour: ({ commit }) => {
    commit('setWelcomeTourMode', false);
  },
  fetchRecurringReport: async ({ commit, state, dispatch }) => {
    if (
      !state.selectedAccount ||
      !state.selectedAccount.id ||
      !state.selectedASN ||
      !state.selectedASN.id
    )
      return;

    commit('setIsShowErrorInfoBanner', false, { root: true });

    const request = requestInstance
      .get(`/report/${state.selectedAccount.id}/${state.selectedASN.id}`)
      .then(data => {
        commit('setRecurringExpression', data.data.cronExpression);
        commit('setRecurringPeriod', data.data.reportPeriod);
        commit('setRecurringLastDate', data.data.lastSent);
        return true;
      })
      .catch(err => {
        if (err.response.status === 404) {
          // When err.response.status is 404, it means there is no recurring report set for this ASN. The recurringExpression and recurringLastDate need to be reset.
          commit('setRecurringExpression', '');
          commit('setRecurringLastDate', null);

          return true;
        } else {
          dispatch('setErrorInfoBanner', '', { root: true });

          return false;
        }
      });
    return request;
  },
  updateRecurringReport: (
    { commit, state, dispatch },
    { expression, period }
  ) => {
    commit('setIsShowErrorInfoBanner', false, { root: true });

    return requestInstance
      .put(`/report/${state.selectedAccount.id}/${state.selectedASN.id}`, {
        memberId: Number(state.selectedAccount.id),
        originASN: Number(state.selectedASN.id),
        cronExpression: expression,
        reportPeriod: period
      })
      .then(data => {
        commit('setRecurringExpression', expression);
        commit('setRecurringPeriod', period);
        return true;
      })
      .catch(err => {
        console.error(err);
        dispatch('setErrorInfoBanner', '', { root: true });
      });
  },
  createRecurringReport: (
    { commit, state, dispatch },
    { expression, period }
  ) => {
    commit('setIsShowErrorInfoBanner', false, { root: true });

    return requestInstance
      .post(`/report/${state.selectedAccount.id}/${state.selectedASN.id}`, {
        memberId: Number(state.selectedAccount.id),
        originASN: Number(state.selectedASN.id),
        cronExpression: expression,
        reportPeriod: period
      })
      .then(data => {
        commit('setRecurringExpression', expression);
        commit('setRecurringPeriod', period);
        return true;
      })
      .catch(err => {
        console.error(err);
        dispatch('setErrorInfoBanner', '', { root: true });
      });
  },
  deleteRecurringReport: ({ commit, dispatch }) => {
    commit('setIsShowErrorInfoBanner', false, { root: true });

    return requestInstance
      .delete(`/report/${state.selectedAccount.id}/${state.selectedASN.id}`)
      .then(data => {
        commit('setRecurringExpression', '');
        commit('setRecurringPeriod', 30);
        return true;
      })
      .catch(err => {
        console.error(err);
        dispatch('setErrorInfoBanner', '', { root: true });
      });
  },
  fetchThemeDataList: context => {
    themeDataListRequestInstance
      .get()
      .then(res => {
        const themeDataList = res.data;

        if (isNonProdEnv) {
          // Adjust omnibarCurrentUserAccountLinks and omnibarCurrentUserMembershipLinks to replace prod links by testing ones.
          const omnibarCurrentUserAccountLinksAdjusted = themeDataList.omnibarCurrentUserAccountLinks.map(
            item => {
              switch (item.label_en_us) {
                case 'MyAPNIC':
                  item.link = profileSectionURLs.myApnicDashboardURL;
                  break;
                case 'Personal profile':
                  item.link = profileSectionURLs.personalSettingsURL;
                  break;
                case 'Passwords & Security':
                  item.link = profileSectionURLs.passwordSecurityURL;
                  break;
                default:
                  break;
              }
              return item;
            }
          );

          // Remove the item of "Log out" when adjusting omnibarCurrentUserMembershipLinks as it'll be handled by our codebase.
          const omnibarCurrentUserMembershipLinksAdjusted = themeDataList.omnibarCurrentUserMembershipLinks
            .filter(item => item.label_en_us !== 'Log out')
            .map(item => {
              switch (item.label_en_us) {
                case 'Link existing membership':
                  item.link = profileSectionURLs.linkExistingMembership;
                  break;
                case 'New membership application':
                  item.link = profileSectionURLs.membershipApplication;
                  break;
                default:
                  break;
              }
              return item;
            });

          const themeDataListAdjusted = {
            ...themeDataList,
            omnibarCurrentUserAccountLinks: omnibarCurrentUserAccountLinksAdjusted,
            omnibarCurrentUserMembershipLinks: omnibarCurrentUserMembershipLinksAdjusted
          };

          context.commit('setThemeDataList', themeDataListAdjusted);
        } else {
          context.commit('setThemeDataList', themeDataList);
        }
      })
      .catch(err => {
        console.error(err);
        dispatch('setErrorInfoBanner', '', { root: true });
      });
  },
  updateAlertsData: (context, asnId) => {
    // In report mode, the updateAlertsData() won't be executed.
    if (process.env.VUE_APP_REPORT_MODE) return;

    // Set isFetchingFiringData to avoid duplicated requests.
    let isFetchingFiringData = true;

    if (
      asnId &&
      asnId.id === updateAlertsDataPreviousExecutionASN &&
      new Date().getTime() - updateAlertsDataPreviousExecutionTimestamp <
        UPDATE_ALERTS_DATA_MIN_EXECUTION_INTERVAL
    )
      isFetchingFiringData = false;

    updateAlertsDataPreviousExecutionASN = asnId ? asnId.id : '';
    updateAlertsDataPreviousExecutionTimestamp = new Date().getTime();

    // Update the amount of firing routing status alerts
    isFetchingFiringData &&
      context.dispatch(
        'alertsRoutingStatusStore/fetchFiringAlertsRoutingStatusAmount',
        {},
        { root: true }
      );

    const currentPath = getPath();

    if (currentPath.indexOf('alerts-suspicious-traffic') < 0) {
      isFetchingFiringData &&
        context.dispatch(
          'alertsSuspiciousTrafficStore/fetchFiringAlerts',
          null,
          {
            root: true
          }
        );
    } else {
      context.dispatch(
        'alertsSuspiciousTrafficStore/fetchAlertListByMemberId',
        null,
        {
          root: true
        }
      );
    }
  },
  searchMemberAccount: (context, query) => {
    if (!query) return;

    const { commit, dispatch, state } = context;

    commit('setLoadingData', true);
    commit('setIsShowErrorInfoBanner', false, { root: true });

    requestInstance
      .get('/summary', { params: { account: query } })
      .then(res => {
        commit('setLoadingData', false);
        commit('setIsSuccessfulSummaryRequest', true, { root: true });

        const formattedResponse = formatSummaryRes(res.data);
        const data = new Meta(formattedResponse);

        storeMemberAccountQueryHistory(state.memberAccountQueryOfAPNICStaff);

        commit('setInitState', data.members);
        commit('setMetaDataFetched', true);
        commit('setMemberAccountQueryOfAPNICStaff', '');

        if (data.members.length > 0) {
          dispatch('selectAccount', data.members[0].id);
        }

        const width = window.innerWidth;
        if (width > 768) {
          commit('setShowSideBar', true, { root: true });
        }
      })
      .catch(err => {
        console.error(err);
        commit('setLoadingData', false);

        // Set error message for 404 error, i.e. invalid member account name.
        if (err.response?.status === 404) {
          dispatch(
            'setErrorInfoBanner',
            `"${err.config?.params?.account?.toUpperCase() ||
              'It'}" is not a valid member account name.`,
            { root: true }
          );
        } else {
          dispatch('setErrorInfoBanner', '', { root: true });
        }
      });
  }
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
};

function filterData(data, type) {
  const {
    current: { start, end }
  } = getStartAndEndDate(type);
  return filterDate(start, end, data);
}

function filterAndParse(data, type) {
  const {
    current: { start, end }
  } = getStartAndEndDate(type);
  const filteredData = filterDate(start, end, data);
  return fillDateGap(start, end, filteredData);
}

function getAccumulativeSum(data, start, end) {
  const filteredData = filterDate(start, end, data);
  let sum = 0;
  filteredData.forEach(i => {
    sum += parseInt(i.count);
  });
  return sum;
}

// return dayjs object
function getStartAndEndDate(type = 'this') {
  if (type === 'this' || type === 'last') {
    let today, back30, back31, back60, back61, back120;

    if (process.env.VUE_APP_REPORT_MODE) {
      today = dayjs(
        dayjs
          .utc()
          .subtract(1, 'day')
          .format(format)
      );
      back30 = dayjs
        .utc()
        .subtract(30, 'day')
        .format(format);
      back31 = dayjs
        .utc()
        .subtract(31, 'day')
        .format(format);
      back60 = dayjs
        .utc()
        .subtract(60, 'day')
        .format(format);
      back61 = dayjs
        .utc()
        .subtract(61, 'day')
        .format(format);
      back120 = dayjs
        .utc()
        .subtract(120, 'day')
        .format(format);
    } else {
      today = dayjs(dayjs.utc().format(format));
      back30 = dayjs
        .utc()
        .subtract(29, 'day')
        .format(format);
      back31 = dayjs
        .utc()
        .subtract(30, 'day')
        .format(format);
      back60 = dayjs
        .utc()
        .subtract(59, 'day')
        .format(format);
      back61 = dayjs
        .utc()
        .subtract(60, 'day')
        .format(format);
      back120 = dayjs
        .utc()
        .subtract(119, 'day')
        .format(format);
    }

    if (type === 'this') {
      return {
        current: { start: back30, end: today },
        compareWith: { start: back60, end: back31 }
      };
    } else {
      return {
        current: { start: back60, end: today },
        compareWith: { start: back120, end: back61 }
      };
    }
  } else {
    // type is a date string
    const start = type.split(',')[0];
    const end = type.split(',')[1];
    return { start: dayjs(start), end: dayjs(end) };
  }
}

function filterDate(start, end, data) {
  return JSON.parse(JSON.stringify(data))
    .filter(
      i =>
        dayjs(i.date).isSameOrAfter(start) && dayjs(i.date).isSameOrBefore(end)
    )
    .sort((a, b) => {
      return dayjs(a.date).isSameOrBefore(dayjs(b.date)) ? -1 : 1;
    });
}

function fillDateGap(start, end, data) {
  const format = 'YYYY-MM-DD';
  let currentDate = dayjs(start);
  const result = [];
  while (!checkSameDate(currentDate, end.add(1, 'day'))) {
    result.push({ x: currentDate.format(format), y: 0 });
    currentDate = currentDate.add(1, 'day');
  }
  const parsedData = data.map(i => {
    return { x: i.date, y: parseInt(i.count) };
  });
  let i = 0,
    j = 0;
  while (i < result.length) {
    if (j < parsedData.length && result[i].x === parsedData[j].x) {
      result[i] = parsedData[j];
      j++;
    }
    i++;
  }
  return result;
}

function checkSameDate(date, target) {
  const format = 'YYYY-MM-DD';
  return date.format(format) === target.format(format);
}

function calculateAccumulativeStats(dateInterval, getters) {
  const { current, compareWith } = getStartAndEndDate(dateInterval);
  const currentStart = current.start;
  const currentEnd = current.end;
  const lastStart = compareWith.start;
  const lastEnd = compareWith.end;
  const currentASN = getAccumulativeSum(
    getters.selectedASN.data.data,
    currentStart,
    currentEnd
  );
  const lastASN = getAccumulativeSum(
    getters.selectedASN.data.data,
    lastStart,
    lastEnd
  );
  const currentEconomy = getAccumulativeSum(
    getters.selectedAccount.economyData.data,
    currentStart,
    currentEnd
  );
  const lastEconomy = getAccumulativeSum(
    getters.selectedAccount.economyData.data,
    lastStart,
    lastEnd
  );
  const currentRegion = getAccumulativeSum(
    getters.selectedAccount.regionData.data,
    currentStart,
    currentEnd
  );
  const lastRegion = getAccumulativeSum(
    getters.selectedAccount.regionData.data,
    lastStart,
    lastEnd
  );
  const asnDelta = calculateDelta(lastASN, currentASN);
  const economyDelta = calculateDelta(lastEconomy, currentEconomy);
  const regionDelta = calculateDelta(lastRegion, currentRegion);
  const dateFormat = 'DD MMM';
  return {
    asn: { current: currentASN, last: lastASN, delta: asnDelta },
    economy: {
      current: currentEconomy,
      last: lastEconomy,
      delta: economyDelta
    },
    region: { current: currentRegion, last: lastRegion, delta: regionDelta },
    thisMonth: `${dayjs(currentStart).format(dateFormat)} / ${dayjs(
      currentEnd
    ).format(dateFormat)}`,
    lastMonth: `${dayjs(lastStart).format(dateFormat)} / ${dayjs(
      lastEnd
    ).format(dateFormat)}`
  };
}

function calculateDelta(oldValue, newValue) {
  if (oldValue === 0 && newValue === 0) {
    return 0;
  }
  if (newValue > oldValue) {
    return (((newValue - oldValue) / oldValue) * 100).toFixed(1);
  } else {
    return (((newValue - oldValue) / oldValue) * 100).toFixed(1);
  }
}

// always fetch 120 days data
function buildHitsRequest({
  memberId,
  asn,
  economy,
  region,
  numberOfDays = NUMBER_OF_DAYS
}) {
  let end;

  if (process.env.VUE_APP_REPORT_MODE) {
    end = dayjs
      .utc()
      .subtract(1, 'day')
      .format(format);
  } else {
    end = dayjs.utc().format(format);
  }

  return requestInstance.get(
    `/hits/${memberId}?end_date=${end}&number_of_days=${numberOfDays}&asn=${asn}&economy=${economy}&region=${region}`
  );
}

function buildPrefixRequest({ memberId, asn, numberOfDays = 30 }) {
  let end;

  if (process.env.VUE_APP_REPORT_MODE) {
    end = dayjs
      .utc()
      .subtract(1, 'day')
      .format(format);
  } else {
    end = dayjs.utc().format(format);
  }

  return requestInstance.get(
    `/prefix/${memberId}?end_date=${end}&number_of_days=${numberOfDays}&origin_as=${asn}`
  );
}

function triggerGTM(event, vars) {
  if (process.env.VUE_APP_REPORT_MODE) {
    return;
  }
  window.dataLayer.push({
    event,
    ...vars
  });
}

function isSelectedASNExistsInSelectedAccount(selectedASN, selectedAccount) {
  if (!selectedAccount.ASNs || selectedAccount.ASNs.length === 0) return false;

  const ASN = selectedAccount.ASNs.find(ASN => ASN.id === selectedASN.id);

  if (!ASN) {
    return false;
  } else {
    return true;
  }
}

function getPath() {
  return (
    router &&
    router.history &&
    router.history.current &&
    router.history.current.path
  );
}

function storeMemberAccountQueryHistory(memberAccount) {
  let history =
    localStorage.getItem(
      LOCAL_STORAGE_ITEM_LIST.MEMBER_ACCOUNT_QUERY_HISTORY
    ) || '[]';

  history = JSON.parse(history);

  history.unshift(memberAccount.toUpperCase());

  const updatedHistory = [...new Set(history)];

  if (updatedHistory.length > MAX_QUERY_HISTORY_ENTRIES_APNIC_STAFF)
    updatedHistory.splice(-1, 1);

  localStorage.setItem(
    LOCAL_STORAGE_ITEM_LIST.MEMBER_ACCOUNT_QUERY_HISTORY,
    JSON.stringify(updatedHistory)
  );
}
